import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, useMemo } from "react"
import {
  ReservationSyncDto,
  SourceKeyDto,
} from "src/features/things/reservationSyncApi"

/**
 * The same page, refusing to state a number (LOCKER-421 `DoD10`, `DoD8`).
 *
 * This is the artboard that is easiest to get wrong, because the tempting
 * implementation is to render the page as normal with zeroes in it. **A zero here
 * reads as "no drift found", which is the exact wrong conclusion** to draw from a
 * run where a source was truncated or failed — and it is the conclusion an
 * operator acts on. So every number this page would otherwise show is an em dash,
 * and the dash is explained rather than left to be interpreted.
 *
 * What it does NOT suppress is the evidence: both sources that answered wrote
 * their raw rows to S3, and the Objects tab still lists them with presigned links.
 * An inconclusive verdict suppresses conclusions, not evidence.
 */

/** What the page would have shown, and now will not. */
const WITHHELD = [
  "Drift total",
  "Four-way presence matrix",
  "Counted-never-drift classes",
  "Worst-first position in the runs list",
  "Sync plan",
]

const SOURCE_LABEL: Record<SourceKeyDto, string> = {
  v1: "SST v1",
  v2: "SST v2",
  ddb: "DynamoDB",
  olap: "SL2 OLAP",
}

export const InconclusiveNotice: FC<{ run: ReservationSyncDto }> = ({ run }) => {
  /**
   * Only the sources that actually caused it. An `UNVERIFIED` source that ended on
   * a short page with the cap clear is NOT a cause — that is the evidence the
   * QUALIFIED level rests on — so listing it here would send the operator to fix
   * something that is working.
   */
  const culprits = useMemo(() => {
    if (!run.sources) return []
    return (Object.keys(run.sources) as SourceKeyDto[]).filter((key) => {
      const source = run.sources![key]
      return (
        source.readState === "FAILED" ||
        source.readState === "TRUNCATED" ||
        (source.readState === "UNVERIFIED" && !source.exhaustedOnShortPage)
      )
    })
  }, [run.sources])

  return (
    <Box data-ui="UI-SL2W-167"
      sx={{
        border: 1,
        borderColor: "error.light",
        borderRadius: 1,
        overflow: "hidden",
        mb: 3,
      }}
    >
      <Stack
        direction="row"
        spacing={1.75}
        alignItems="flex-start"
        sx={{
          p: 2.75,
          borderBottom: 1,
          borderColor: "error.light",
          bgcolor: "action.hover",
        }}
      >
        <WarningAmberIcon color="error" />
        <Box>
          <Typography sx={{ fontWeight: 600, color: "error.main", mb: 0.75 }}>
            Inconclusive — no count is shown for this run, and none was stored.
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: "95ch", lineHeight: 1.6 }}>
            {culprits.length === 1 ? "One of" : `${culprits.length} of`} the four
            sources came back incomplete
            {culprits.length
              ? ` (${culprits.map((c) => `${SOURCE_LABEL[c]} · ${run.sources![c].readState.toLowerCase()}`).join(", ")})`
              : ""}
            , so every number this page would otherwise render — drift totals,
            per-class counts, the presence matrix, the per-locker ranking — would be
            a statement about rows nobody read. A partial read presented as a drift
            count is the failure this verdict exists to prevent. Re-run the recon;
            this record stays as evidence that it was attempted.
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          p: 2.75,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 3.5,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: "0.06em", color: "text.disabled" }}
          >
            WHAT WOULD HAVE BEEN SHOWN
          </Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableBody>
              {WITHHELD.map((label) => (
                <TableRow key={label}>
                  <TableCell sx={{ border: 0, borderBottom: 1, borderColor: "divider" }}>
                    {label}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: 0,
                      borderBottom: 1,
                      borderColor: "divider",
                      fontFamily: "monospace",
                      fontSize: "1.1rem",
                      color: "text.disabled",
                    }}
                  >
                    —
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.5, color: "text.secondary", lineHeight: 1.55 }}
          >
            The em dash is deliberate and is not a zero. A zero here would read as
            “no drift found”, which is the exact wrong conclusion.
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: "0.06em", color: "text.disabled" }}
          >
            WHAT TO DO
          </Typography>
          <Box component="ol" sx={{ pl: 2.5, mt: 1, mb: 2 }}>
            {culprits.map((key) => (
              <Typography
                key={key}
                component="li"
                variant="body2"
                sx={{ lineHeight: 1.75, mb: 0.5 }}
              >
                <strong>{SOURCE_LABEL[key]}</strong> —{" "}
                {(
                  run.sources![key].error ??
                  (run.sources![key].readState === "TRUNCATED"
                    ? "the walk hit its page cap. A retry at the same settings changes nothing; raise the cap or narrow the run"
                    : "completeness could not be confirmed and the walk did not end short, so the read may be partial")
                ).replace(/\.$/, "")}
                . Re-run, or re-run with <code>--skip-{key}</code> for an honest
                three-way run — a skipped source is not applicable rather than
                failed, so it does not downgrade the verdict.
              </Typography>
            ))}
            <Typography component="li" variant="body2" sx={{ lineHeight: 1.75 }}>
              Re-run the recon. This record stays; it is not overwritten.
            </Typography>
          </Box>
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.75 }}>
            <Typography variant="caption" sx={{ lineHeight: 1.55 }}>
              <strong>Objects are still stored and still readable.</strong> Every
              source that succeeded wrote its raw rows to S3, and the Objects tab
              lists them with presigned links. An inconclusive verdict suppresses
              conclusions, not evidence.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
